import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

/** A configured connection method, summarized for a protocol card. */
export interface ConnectionMethodSummary {
  id: string;
  sessionType: string;
  host: string;
  port: number | null;
  credentialId: string | null;
  credentialName: string | null;
  priority: number;
  enabled: boolean;
  /** Vendor-profile `service` role. `sessionType` is the transport (http vs
   *  https); this is what the endpoint *is*, and it is what lets a device
   *  serving both a web UI and a REST API over https show two distinct cards.
   *  Null on rows predating the field. */
  service: string | null;
}

/** Live + passive health for a configured protocol. */
export interface ConnectionHealthSummary {
  /** `healthy | failing | degraded | cooldown | unknown` */
  status: string;
  lastSuccess: string | null;
  lastFailure: string | null;
  failureReason: string | null;
  /**
   * TLS compatibility profile in use: `modern`, `compat` or `legacy`.
   * `null` for non-TLS methods, and for HTTPS that never had to downgrade.
   *
   * An observation about the device, not a setting. The gateway walks the
   * ladder on its own when a device refuses a standards-compliant
   * handshake, so surfacing it here is the only way an operator learns
   * their switch is being reached over weakened TLS.
   */
  tlsProfile: string | null;
  successCount: number;
  failureCount: number;
  avgLatencyMs: number | null;
  gatewayName: string | null;
  gatewayStatus: string | null;
}

export interface ConnectionSetupInfo {
  /** `enable_netconf | enable_rest_api`, or null when there's no auto-enable. */
  autoEnableAction: string | null;
  requiresSshCredential: boolean;
  hasSshCredential: boolean;
}

/** One protocol's fused capability + configured + health + next action. */
export interface ConnectionProtocol {
  /** `snmp | ssh | netconf | rest_api | web_ui | telnet | console` */
  kind: string;
  label: string;
  supported: boolean;
  configured: boolean;
  method: ConnectionMethodSummary | null;
  health: ConnectionHealthSummary | null;
  setup: ConnectionSetupInfo;
}

interface ConnectionsOverviewResponse {
  deviceId: string;
  protocols: ConnectionProtocol[];
}

const backendApi = config.backendApi;

/**
 * The guided per-protocol connections overview for a device: what the device
 * can do (profile), what's set up (connection methods), and whether it's
 * actually working (health). Query key `['device-connections', deviceId]`.
 */
export function useDeviceConnections(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<ConnectionProtocol[]>({
    queryKey: ['device-connections', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<ConnectionsOverviewResponse>(
        `${backendApi}/api/devices/${deviceId}/connections`,
        { withCredentials: true }
      );
      return response.data.protocols;
    },
    enabled: !!deviceId && contextReady,
  });
}
